import React, { FC, useEffect, useMemo } from 'react';

import Modal from '~components/ui/Modal';

import { BuyResponse } from '~types';

interface Props {
  url: string;
  apiResponse: BuyResponse;
  onSuccess: (data: ThreeDSValidationResponse) => void;
  onError: (error: ThreeDSError, apiResponse: BuyResponse) => void;
}

export type ThreeDSValidationResponse = {
  payment_status: string;
  order: { id: string };
};

export type ThreeDSError = {
  message: string;
};

type ThreeDSMessage = Partial<ThreeDSValidationResponse> & {
  error?: string | { message?: string };
};

const ENV_URL = 'https://3ds-pay.conekta.com';
const MESSAGE_LISTENER_CAPTURE = false;
const THREE_DS_TIMEOUT_MS = 10 * 60 * 1000;
const DEFAULT_ERROR_MESSAGE =
  'No fue posible validar la autenticación bancaria. Intenta completar la compra nuevamente.';

const getThreeDSErrorMessage = (error: ThreeDSMessage['error']) => {
  if (typeof error === 'string') return error;

  return error?.message || DEFAULT_ERROR_MESSAGE;
};

const parseThreeDSMessage = (data: MessageEvent['data']): ThreeDSMessage | null => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as ThreeDSMessage;
    } catch {
      return null;
    }
  }

  if (!data || typeof data !== 'object') return null;

  return data as ThreeDSMessage;
};

const getThreeDSUrl = (url: string) => {
  try {
    const threeDSUrl = new URL(url);
    threeDSUrl.searchParams.set('source', 'embedded');
    return threeDSUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}source=embedded`;
  }
};

const ThreeDSValidation: FC<Props> = ({ url, onSuccess, onError, apiResponse }) => {
  const iframeUrl = useMemo(() => getThreeDSUrl(url), [url]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const timeoutId = window.setTimeout(() => {
        onError(
          {
            message:
              'No recibimos respuesta del banco para la autenticación 3DS. Intenta nuevamente.',
          },
          apiResponse,
        );
      }, THREE_DS_TIMEOUT_MS);

      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== ENV_URL) return;
        const message = parseThreeDSMessage(event.data);
        if (!message) return;

        const { payment_status, error, order } = message;
        if (error || payment_status !== 'paid') {
          onError({ message: getThreeDSErrorMessage(error) }, apiResponse);
          return;
        }

        if (!order?.id) {
          onError(
            {
              message: '3DS fue aprobada, pero no recibimos el identificador de la orden.',
            },
            apiResponse,
          );
          return;
        }

        onSuccess({ payment_status, order: { id: order.id } });
      };
      window.addEventListener('message', messageHandler, MESSAGE_LISTENER_CAPTURE);
      return () => {
        window.clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler, MESSAGE_LISTENER_CAPTURE);
      };
    }
  }, [apiResponse, onError, onSuccess]);

  return (
    <Modal open preventClose className="px-0">
      <iframe
        src={iframeUrl}
        className="w-full max-w-[500px] md:w-[500px] h-[600px] rounded-2xl shadow-2xl bg-white "
      ></iframe>
    </Modal>
  );
};

const MemoizedThreeDSValidation = React.memo(ThreeDSValidation);
MemoizedThreeDSValidation.displayName = 'ThreeDSValidation';

export default MemoizedThreeDSValidation;
